<template>
  <div>
    <div class="row">
      <div class="col-9 d-flex flex-column gap-1 py-2">
        <div :class="positionClass">{{ actedByPosition }}</div>
        <span class="approval-description" v-html="approvalHistoryDescriptionHtml"/>
        <template v-if="step.history">
          <span :class="eventAtClass">{{ formattedEventAt }}</span>
          <b-link
              v-if="step.history.file_url"
              :href="approvalHistoryAttachmentUrl(step.history.file_url)"
              class="approval-attachment-link"
              rel="noopener noreferrer"
              target="_blank"
          >
            {{ lodash.startCase($t('purchase_plans.approval_histories.see_attachment')) }}
          </b-link>
        </template>
      </div>
      <div class="col-3 d-flex align-items-center justify-content-end">
        <status-badge
            v-if="step.status"
            :status="badgeStatus"
            entity="purchase_plan"
            type="approval_status"
        ></status-badge>
        <span v-else>-</span>
      </div>
      <div v-if="historyReason" class="col-12">
        <p :class="['step-reason mt-1 mb-0', {
          'step-reason-rejected': step.status === 'rejected',
        }]">"{{ historyReason }}"</p>
      </div>
      <div v-if="cancellation" class="col-12">
        <div class="step-cancellation-date mt-1">{{ cancellationEventAt }}</div>
        <b-link class="approval-attachment-link d-inline-block mt-1" @click.prevent="showCancellationReason = !showCancellationReason">
          {{ lodash.startCase($t('purchase_plans.approval_histories.see_reason')) }}
        </b-link>
        <div v-if="showCancellationReason" class="step-cancellation mt-1">
          <p v-if="cancellation.reason" class="step-cancellation-reason mb-0">"{{ cancellation.reason }}"</p>
          <div class="step-cancellation-title" v-html="cancellationDescriptionHtml"/>
        </div>
      </div>
    </div>
    <!-- <hr v-if="!isLast"/> -->
  </div>
</template>

<script>
import StatusBadge from "@/components/badges/status-badge.vue";

const stepTextVariants = {
  pending: {
    positionClass: 'approver-position approver-position--default',
    eventAtClass: 'approval-event-at approval-event-at--muted',
  },
  approved: {
    positionClass: 'approver-position approver-position--default',
    eventAtClass: 'approval-event-at approval-event-at--muted',
  },
  rejected: {
    positionClass: 'approver-position approver-position--danger',
    eventAtClass: 'approval-event-at approval-event-at--danger',
  },
  waiting: {
    positionClass: 'approver-position approver-position--waiting',
    eventAtClass: 'approval-event-at approval-event-at--muted',
  },
}

export default {
  name: 'purchase-plan-approval-progress-step',
  components: {
    StatusBadge,
  },
  props: {
    step: {
      type: Object,
      required: true,
    },
    isLast: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showCancellationReason: false,
    }
  },
  computed: {
    performedBy() {
      return this.step.history?.performed_by ?? null
    },
    actedByPosition() {
      return this.performedBy?.position ?? this.step.approver?.position ?? '-'
    },
    approvalHistoryDescriptionHtml() {
      let beforeName
      let name

      if (this.step.status === 'approved' || this.step.status === 'rejected') {
        const labelKey = this.step.status === 'approved' ? 'purchase_plans.approved_by' : 'purchase_plans.rejected_by'
        beforeName = this.lodash.upperFirst(this.$t(labelKey))
        name = this.performedBy?.name ?? this.step.approver?.name ?? '-'
      } else {
        beforeName = this.lodash.upperFirst(this.$t('purchase_plans.waiting_for_approval_by'))
        name = this.step.approver?.name ?? '-'
      }

      return `${beforeName}: <b>${this.lodash.escape(name)}</b>`
    },
    statusVariant() {
      return stepTextVariants[this.step.status] || stepTextVariants.pending
    },
    positionClass() {
      return this.statusVariant.positionClass
    },
    eventAtClass() {
      return this.statusVariant.eventAtClass
    },
    formattedEventAt() {
      return this.step.history ? this.$moment(this.step.history.event_at).format('DD-MM-YYYY') : ''
    },
    historyReason() {
      return this.step.history?.reason || null
    },
    cancellation() {
      return this.step.cancellation || null
    },
    badgeStatus() {
      const isUnresolved = ['current', 'pending'].includes(this.step.status)

      return this.cancellation && isUnresolved ? 'cancelled' : this.step.status
    },
    cancellationDescriptionHtml() {
      const name = this.lodash.escape(this.cancellation?.performed_by?.name ?? '-')

      return `${this.lodash.upperFirst(this.$t('purchase_plans.cancelled_by'))}: <b>${name}</b>`
    },
    cancellationEventAt() {
      return this.cancellation ? this.$moment(this.cancellation.event_at).format('DD-MM-YYYY') : ''
    },
  },
  methods: {
    approvalHistoryAttachmentUrl(filePath) {
      return encodeURI(filePath)
    },
    extractPlanHistoryReason(planHistory) {
      return planHistory?.reason ?? '-'
    },
  },
}
</script>

<style lang="scss" scoped>
.approver-position {
  font: 600 12px/14px Inter;

  &--default {
    color: #171717;
  }

  &--danger {
    color: #DC2626;
  }

  &--waiting {
    color: #DC2626;
  }
}

.approval-event-at {
  font: 400 10px/12px Inter;

  &--muted {
    color: #6C7589;
  }

  &--danger {
    color: #F87171;
  }
}

.approval-description {
  font: 400 10px/12px Inter;
  color: #98A2B3;
  font-style: italic;
}

.approval-attachment-link {
  font: 700 10px/14px Inter;
  color: #1664D8;
}

.step-reason {
  border-radius: 8px;
  font: 400 10px/12px Inter;
  padding: 8px;
  background-color: #F3F4F6;
  border: 1px solid #E5E7EB;
  color: #111827;
}

.step-cancellation {
  border-radius: 8px;
  padding: 8px;
  background-color: #F3F4F6;
  border: 1px solid #E5E7EB;
}

.step-cancellation-title {
  font: 400 12px/14px Inter;
  color: #667085;
  font-style: italic;
}

.step-cancellation-date {
  font: 400 10px/12px Inter;
  color: #171717;
}

.step-cancellation-reason {
  margin-bottom: 4px !important;
  font: 400 12px/12px Inter;
  color: #6C7589;
}

.step-reason-rejected {
  background-color: #FEE2E2;
  border: 1px solid #F87171;
  color: #DC2626;
}

</style>
